import { useEffect, useMemo } from 'react'

import { Flex, Header, Tabs, Text } from 'govwell-ui'
import { TabItem } from 'govwell-ui/components/Tabs/Tabs'
import { useLocalStorage } from 'usehooks-ts'

import AIPageContainer from 'src/components/AIAssistant/AIPageContainer'
import UnsavedChangesProtector from 'src/components/UnsavedChangesProtector/UnsavedChangesProtector'
import { useMyOrg } from 'src/layouts/AppStateContextLayout/utils'
import AIAssistantInstallation from 'src/pages/AIAssistantSettingsPage/AIAssistantInstallation'
import CodeSnippet from 'src/pages/AIAssistantSettingsPage/CodeSnippet'
import EmailInsightsTab from 'src/pages/AIAssistantSettingsPage/EmailInsightsTab'
import { useLauncherSettingsForm } from 'src/pages/AIAssistantSettingsPage/launcherSettingsForm'
import TeamTab from 'src/pages/AIAssistantSettingsPage/TeamTab'
import { LocalStorageKey } from 'src/types'

enum AIAssistantSettingsTabKey {
  Team = 'Team',
  EmailInsights = 'EmailInsights',
  Installation = 'Installation',
}

const AIAssistantSettingsPage = () => {
  const organization = useMyOrg()
  const { aiChatScriptUrl, isAIChatEnabled } = organization ?? {}

  const [selectedTabKey, setSelectedTabKey] = useLocalStorage<AIAssistantSettingsTabKey>(
    LocalStorageKey.AIAssistantSettingsTabKey,
    AIAssistantSettingsTabKey.Team
  )
  // handle the removal of the Usage tab
  useEffect(() => {
    if (!Object.values(AIAssistantSettingsTabKey).includes(selectedTabKey)) {
      setSelectedTabKey(AIAssistantSettingsTabKey.Team)
    }
  }, [selectedTabKey, setSelectedTabKey])
  const { form, onDiscardChanges, onSave } = useLauncherSettingsForm()

  const tabs: TabItem<AIAssistantSettingsTabKey>[] = useMemo(
    () => [
      {
        key: AIAssistantSettingsTabKey.Team,
        label: 'Team',
        renderChildren: () => <TeamTab />,
      },
      {
        key: AIAssistantSettingsTabKey.EmailInsights,
        label: 'Email Insights',
        renderChildren: () => <EmailInsightsTab />,
      },
      {
        key: AIAssistantSettingsTabKey.Installation,
        label: 'Installation',
        renderChildren: () => (
          <Flex direction="column" gap={4}>
            <Flex direction="column" gap={2} align="start">
              <Text.Body>
                You can install the GovWell Community Assistant as an embedded widget in your
                organization&apos;s website. Configure the launcher here, then copy and paste the
                Install Script below.
              </Text.Body>
              <Text.Body>Contact the GovWell team via Intercom if you need help.</Text.Body>
            </Flex>
            <UnsavedChangesProtector
              hasUnsavedChanges={form.state.isDirty}
              onDiscardChanges={onDiscardChanges}
              onSave={onSave}
            >
              <AIAssistantInstallation isAIChatEnabled={!!isAIChatEnabled} form={form} />
            </UnsavedChangesProtector>
            <Flex direction="column" gap={2} align="start">
              <Header
                title="Install Script"
                subtitle="Paste this code in your web page. The Community Assistant launcher button will appear if the current URL matches one of the allowed URLs above."
              />
              <CodeSnippet
                code={`<script src="${aiChatScriptUrl}" type="text/javascript"></script>`}
              />
            </Flex>
          </Flex>
        ),
      },
    ],
    [aiChatScriptUrl, form, isAIChatEnabled, onDiscardChanges, onSave]
  )

  return (
    <AIPageContainer
      title="Community Assistant Settings"
      subtitle="Configure Community Assistant access and installation"
    >
      <Tabs
        selectedTabKey={selectedTabKey}
        tabs={tabs}
        onSelectedTabKeyChange={setSelectedTabKey}
      />
      {tabs.find((i) => i.key === selectedTabKey)?.renderChildren?.()}
    </AIPageContainer>
  )
}

export default AIAssistantSettingsPage
